import {Directive, input, type Signal} from "@angular/core";
import type {AbstractControl, AbstractControlDirective, ValidationErrors, Validator} from "@angular/forms";
import {NG_VALIDATORS} from "@angular/forms";
import {equals, identityEquals, isEquatable} from "../helpers/Equatable";
import type {OptionalNullable} from "../helpers/TypeHelpers";

type ValueComparisonOtherField = AbstractControlDirective | string | readonly (string | number)[];

abstract class ValueComparisonValidator implements Validator {
  public abstract readonly otherField: Signal<ValueComparisonOtherField>;

  protected abstract validateValues(lhs: unknown, rhs: unknown): ValidationErrors | null;

  private isNullOrEmpty(value: unknown): value is OptionalNullable<""> {
    return value === null || value === undefined || value === "";
  }

  protected areValuesEqual(lhs: unknown, rhs: unknown): boolean {
    return isEquatable(lhs) && isEquatable(rhs) ? equals(lhs, rhs) : equals(lhs, rhs, identityEquals);
  }

  public validate(control: AbstractControl): ValidationErrors | null {
    const otherField = this.otherField();
    const otherControl = typeof otherField === "string" || Array.isArray(otherField)
      ? control.root.get(otherField) : otherField.control;
    if (!otherControl || this.isNullOrEmpty(control.value) || this.isNullOrEmpty(otherControl.value)) return null;
    return this.validateValues(control.value, otherControl.value);
  }
}

@Directive({
  selector: "input[rfValueEqual]",
  providers: [
    {provide: NG_VALIDATORS, useExisting: ValueEqualValidator, multi: true},
  ],
})
export class ValueEqualValidator extends ValueComparisonValidator {
  public override readonly otherField = input.required<ValueComparisonOtherField>({alias: "rfValueEqual"});

  protected override validateValues(lhs: unknown, rhs: unknown): ValidationErrors | null {
    return this.areValuesEqual(lhs, rhs) ? null : {valueEqual: {otherValue: rhs}};
  }
}

@Directive({
  selector: "input[rfValueNotEqual]",
  providers: [
    {provide: NG_VALIDATORS, useExisting: ValueNotEqualValidator, multi: true},
  ],
})
export class ValueNotEqualValidator extends ValueComparisonValidator {
  public override readonly otherField = input.required<ValueComparisonOtherField>({alias: "rfValueNotEqual"});

  protected override validateValues(lhs: unknown, rhs: unknown): ValidationErrors | null {
    return this.areValuesEqual(lhs, rhs) ? {valueNotEqual: true} : null;
  }
}
